import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {PageQueryInterface} from "../../../_models/page.interface";
import {a} from "@fullcalendar/core/internal-common";


@Injectable({
  providedIn: 'root'
})
export class AdminService {

  private ADMIN_API: string;
  private FILE_API: string;
  private ROLE_API: string;
  constructor(private http: HttpClient) {
    this.ADMIN_API = `${environment.apiUrl}/admin/`;
    this.FILE_API = `${environment.apiUrl}/file/`;
    this.ROLE_API = `${environment.apiUrl}/role/`;
  }

  async getAllUsers(pageQuery: PageQueryInterface): Promise<any> {
    return await this.http.post(this.ADMIN_API + `get-all-users`, pageQuery).toPromise();
  }

  async enableDisableUser(payload: any): Promise<any> {
    return await this.http.post(this.ADMIN_API + `enable-users`, payload).toPromise();
  }

  async revokeAdmin(payload: any): Promise<any> {
    return await this.http.post(this.ADMIN_API + `revoke-admin-users`, payload).toPromise();
  }

  async makeAdmin(payload: any): Promise<any> {
    return await this.http.post(this.ADMIN_API + `make-admin-users`, payload).toPromise();
  }

  async getUserById(id: number): Promise<any> {
    return await this.http.get(this.ADMIN_API + `get-all-users/${id}`).toPromise();
  }

  async getWalletSupport(pageQuery: PageQueryInterface): Promise<any> {
    return await this.http.post(this.ADMIN_API + `get-wallet-supports`, pageQuery).toPromise();
  }

  async getWalletSupportDetails(id: number): Promise<any> {
    return await this.http.get(this.ADMIN_API + `wallet-supports/${id}`).toPromise();
  }

  async replyWalletSupportTicket(message: string, walletSupportId: number, hasAttachment: boolean): Promise<any> {
    return await this.http.post<any>(this.ADMIN_API + 'reply-wallet-support-ticket', {
      message: message,
      walletSupportId: walletSupportId,
      hasAttachment: hasAttachment,
    }).toPromise();
  }

  async closeWalletSupportTicket(id: number): Promise<any> {
    return await this.http.post(this.ADMIN_API + `close-wallet-support-ticket`, {walletSupportId: id}).toPromise();
  }

  async uploadFileAttachmentAdmin(fd: FormData, id: number): Promise<any> {
    return await this.http.post<any>(`${this.FILE_API}upload-attachment-admin?id=${id}`, fd, {}).toPromise();
  }

  async getUserCEUs(pageQuery: PageQueryInterface, userId: number): Promise<any> {
    return await this.http.post(this.ADMIN_API + `get-user-ceus/${userId}/v2`, pageQuery).toPromise();
  }

  async getUserCEUById(documentId: number, userId: number): Promise<any> {
    return await this.http.get(this.ADMIN_API + `get-user-ceus/${documentId}/${userId}`).toPromise();
  }

  async getUserCertifications(pageQuery: PageQueryInterface, userId: number): Promise<any> {
    return await this.http.post(this.ADMIN_API + `get-user-certifications/${userId}`, pageQuery).toPromise();
  }

  async getUserCertificationList(userId: number): Promise<any> {
    return await this.http.get(this.ADMIN_API + `get-user-certifications/${userId}`).toPromise();
  }

  async getAdminCheckActiveSubscription(userId: number): Promise<any> {
    return await this.http.get(this.ADMIN_API + `active-subscription/${userId}`).toPromise();
  }

  async getAllUserRoles(): Promise<any> {
    return await this.http.get(this.ROLE_API + `get-all`).toPromise();
  }

  async getAllProducts(): Promise<any> {
    return await this.http.get(this.ADMIN_API + `get-all-products`).toPromise();
  }

  async getUserTranscript(id: number): Promise<any> {
    return await this.http.get(this.FILE_API + `pdf/transcript-admin/${id}`, {
      responseType: 'blob',
    }).toPromise();
  }

  async saveValidationCheck(payload: any): Promise<any> {
    return await this.http.post<any>(this.ADMIN_API + `save-validation-check`, payload).toPromise();
  }

  async uploadFile(fd: FormData, certificationId: number, documentType: string, providerActivityId: number, userId: number, validationCheck: any): Promise<any> {
    fd.append("validationCheck", new Blob([JSON.stringify(validationCheck)], { type: "application/json" }));
    return await this.http.post<any>(`${this.FILE_API}admin-upload-cert?certificationId=${certificationId}&documentType=${documentType}&userId=${userId}&activityId=${providerActivityId}`, fd, {}).toPromise();
  }

  async updateFile(fd: FormData, documentId: number, certificationId: number, documentType: string, providerActivityId: number, userId: number, validationCheck: any): Promise<any> {
    fd.append("validationCheck", new Blob([JSON.stringify(validationCheck)], { type: "application/json" }));
    return await this.http.post<any>(`${this.FILE_API}admin-update-cert?documentId=${documentId}&certificationId=${certificationId}&documentType=${documentType}&userId=${userId}&activityId${providerActivityId}`, fd, {}).toPromise();
  }

  async updateAccount(fullName: string, phoneNumber: string, userId: number): Promise<any> {
    return await this.http.post<any>(`${this.ADMIN_API}update-user-account`, {fullName, mobileNumber: phoneNumber, userId}).toPromise();
  }

  async getUpcomingCEUs(pageQuery: PageQueryInterface): Promise<any> {
    return await this.http.post(this.ADMIN_API + 'get-upcoming-ceus', pageQuery).toPromise();
  }

  async addNewCertification(payload: any): Promise<any> {
    return await this.http.post(this.ADMIN_API + 'add-certification', payload).toPromise();
  }

  async deleteCertification(id: number): Promise<any> {
    return await this.http.delete(this.ADMIN_API + 'delete-certification/'+id).toPromise();
  }

  async editCertification(payload: any): Promise<any> {
    return await this.http.put(this.ADMIN_API + 'edit-certification', payload).toPromise();
  }

  async addNewCeuEvent(payload: any): Promise<any> {
    return await this.http.post(this.ADMIN_API + 'add-ceu-event', payload).toPromise();
  }

  async addProvider(providerName: string, forModule: string): Promise<any> {
    return await this.http.post(this.ADMIN_API + 'add-provider', {providerName, forModule}).toPromise();
  }

  async deleteCeuEvent(id: number): Promise<any> {
    return await this.http.delete(this.ADMIN_API + 'delete-ceu-event/'+id).toPromise();
  }

  async editCeuEvent(payload: any): Promise<any> {
    return await this.http.put(this.ADMIN_API + 'edit-ceu-event', payload).toPromise();
  }
}

